import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeCH from '@angular/common/locales/de-CH';
import localeEs from '@angular/common/locales/es';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeFrCH from '@angular/common/locales/fr-CH';
import localeIt from '@angular/common/locales/it';
import localeItCh from '@angular/common/locales/it-CH';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localeSv from '@angular/common/locales/sv';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  provideZoneChangeDetection
} from '@angular/core';
import {
  provideAnimations
} from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { RouterModule } from '@angular/router';
import { environment } from '@environments/environment';
import { AuthTokenInterceptor } from '@interceptor/auth-token.interceptor';
import { GlobalErrorHandler } from '@interceptor/global-error-handler.interceptor';
import { HttpInterceptor } from '@interceptor/http-interceptor';
import { TranslateCompiler, TranslateLoader, provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { BrandTranslateCompiler } from './shared/compilers/brand-translate-compiler';
import { TraceService } from '@sentry/angular';
import { CalendarUtils, DateAdapter, provideCalendar } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { provideNgxStripe } from 'ngx-stripe';
import { Observable, of } from 'rxjs';
import de_CH from '../assets/i18n/de_CH.json';
import en_US from '../assets/i18n/en_US.json';
import es_ES from '../assets/i18n/es_ES.json';
import fi_FI from '../assets/i18n/fi_FI.json';
import fr_CH from '../assets/i18n/fr_CH.json';
import it_CH from '../assets/i18n/it_CH.json';
import nl_NL from '../assets/i18n/nl_NL.json';
import pl_PL from '../assets/i18n/pl_PL.json';
import sv_SE from '../assets/i18n/sv_SE.json';

registerLocaleData(localeDe, 'de');
registerLocaleData(localeDeCH, 'ch');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeFrCH, 'fr_CH');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeItCh, 'it_CH');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeSv, 'sv');

// Translations are bundled like the widget repo — one config for both modes, no HTTP i18n.
const TRANSLATION_MAP: Record<string, any> = {
  de_CH, en_US, fi_FI, fr_CH, it_CH, nl_NL, pl_PL, es_ES, sv_SE,
};

export class CustomTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return of(TRANSLATION_MAP[lang] || TRANSLATION_MAP[environment.defaultLocale]);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 21 defaults to zoneless. This app renders off plain fields mutated in
    // setTimeout/RxJS callbacks rather than signals, so without zone.js nothing ever
    // repaints after the first render and the widget hangs on the loading skeleton.
    provideZoneChangeDetection(),
    { provide: LOCALE_ID, useValue: environment.defaultLocale.replace('_', '-').toLowerCase() },
    CalendarUtils,
    CurrencyPipe,
    provideAnimations(),
    provideRouter([]),
    provideTranslateService({
      loader: provideTranslateLoader(CustomTranslateLoader),
      compiler: { provide: TranslateCompiler, useClass: BrandTranslateCompiler }
    }),
    provideCalendar({ provide: DateAdapter, useFactory: adapterFactory }),
    provideHttpClient(
      withInterceptors([HttpInterceptor, AuthTokenInterceptor])
    ),
    provideNgxStripe(environment.stripe.publishableKey),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: APP_INITIALIZER, useFactory: () => () => { }, deps: [TraceService], multi: true, }
  ],
};
